import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Box, InputBase, Typography, lighten, alpha } from '@mui/material';

import { MAX_JOURNAL_TEXT_CHARS } from 'constants/common';
import { DataGridErrorHelperText } from 'components/simple/DataGrid/DataGridErrorHelperText';

const styles = {
  cellBox: {
    width: '100%',
    height: '100%',
    paddingBlock: 2,
  },
  placeholderCellBox: {
    padding: 'unset',
  },
  innerCellBox: ({ palette }) => ({
    paddingInline: 1,
    border: `2px solid ${palette.colors.liteLilac}`,
    borderRadius: 2.5,
    height: '100%',
  }),
  errorCellBox: ({ palette }) => ({
    position: 'relative',
    borderColor: `${palette.error.main} !important`,
  }),
  floatingWrapper: ({ palette }) => ({
    position: 'absolute',
    top: 8,
    left: 0,
    right: 0,
    zIndex: 10,
    borderRadius: 2.5,
    backgroundColor: lighten(palette.colors.lightPale, 0.4),
    border: `2px solid ${palette.colors.darkPale}`,
    boxShadow: `0px 4px 4px 0px ${alpha(palette.common.black, 0.25)}`,
  }),
  anchor: {
    position: 'relative',
    width: '100%',
    height: '100%',
  },
  input: ({ typography }) => ({
    ...typography.body2,
    width: '100%',
    alignItems: 'flex-start',
    fontSize: 'inherit',
    fontFamily: 'inherit',
    lineHeight: 'inherit',
  }),
  floatingInput: {
    padding: 1,
  },
};

export const DataGridMultilineEditCell = ({
  params = undefined,
  value = '',
  floating = false,
  isRequired = true,
  minRows = 1,
  maxRows = undefined,
}) => {
  const isEditMode = !!params;

  const inputRef = useRef(null);

  useEffect(() => {
    if (isEditMode && inputRef.current) {
      inputRef.current.focus();
      const { length } = inputRef.current.value;

      inputRef.current.setSelectionRange(length, length);
    }
  }, [isEditMode]);

  const handleChange = (event) => {
    if (!params) {
      return;
    }

    params.api.setEditCellValue({
      id: params.id,
      field: params.field,
      value: event.target.value,
    });
  };

  if (!isEditMode) {
    return value ? (
      <Typography variant="body2">{value}</Typography>
    ) : (
      <Box sx={[styles.cellBox, styles.placeholderCellBox]}>
        <Box sx={[styles.innerCellBox, isRequired && styles.errorCellBox]}>
          {isRequired && <DataGridErrorHelperText />}
        </Box>
      </Box>
    );
  }

  const inputElement = (
    <InputBase
      multiline
      fullWidth
      inputRef={inputRef}
      value={params.value ?? ''}
      onChange={handleChange}
      minRows={minRows}
      maxRows={maxRows}
      inputProps={{ maxLength: MAX_JOURNAL_TEXT_CHARS }}
      sx={[styles.input, floating && styles.floatingInput]}
    />
  );

  if (floating) {
    return (
      <Box sx={styles.anchor}>
        <Box sx={[styles.floatingWrapper, params.error && styles.errorCellBox]}>
          {inputElement}
          {params.error && <DataGridErrorHelperText />}
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={styles.cellBox}>
      <Box sx={[styles.innerCellBox, params.error && styles.errorCellBox]}>
        {inputElement}
        {params.error && <DataGridErrorHelperText />}
      </Box>
    </Box>
  );
};

DataGridMultilineEditCell.propTypes = {
  params: PropTypes.shape({
    value: PropTypes.string,
    error: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    field: PropTypes.string.isRequired,
    api: PropTypes.shape({
      setEditCellValue: PropTypes.func.isRequired,
    }).isRequired,
  }),
  value: PropTypes.string,
  floating: PropTypes.bool,
  isRequired: PropTypes.bool,
  minRows: PropTypes.number,
  maxRows: PropTypes.number,
};
